import { useMutation, useQuery } from "@tanstack/react-query"
import { Alert, Button, Field, Input, type WizardErrors } from "@ternary/ui"
import { type FormEvent, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { completeAdminCredential, loadAdminCredentialToken } from "../api.js"
import { passwordErrors, SetupPage } from "../components/setup-page.js"

interface AdminCredentialPageProps {
  purpose: "create" | "reset"
}

/**
 * The password step of a reset link or a team invitation. A link that is invalid, expired or
 * could not be checked says so and points back to sign in, where a new reset can be asked for.
 */
export function AdminCredentialPage({ purpose }: AdminCredentialPageProps) {
  const token = useParams().token ?? ""
  const [confirmPassword, setConfirmPassword] = useState("")
  const [password, setPassword] = useState("")
  const [errors, setErrors] = useState<WizardErrors>({})
  const headingRef = useRef<HTMLHeadingElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const confirmRef = useRef<HTMLInputElement>(null)
  const status = useQuery({
    queryFn: ({ signal }) => loadAdminCredentialToken(purpose, token, { signal }),
    queryKey: ["admin-credential-token", purpose, token],
  })
  const complete = useMutation({
    mutationFn: () =>
      completeAdminCredential(purpose, token, {
        confirmPassword,
        password,
      }),
    // The submit button leaves with the form, so focus goes to the heading above the message.
    onSuccess: () => headingRef.current?.focus(),
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const next = passwordErrors({ confirmPassword, password })
    setErrors(next)
    // The first field to fix takes focus, so its error is read out with it.
    if (next.password) passwordRef.current?.focus()
    else if (next.confirmPassword) confirmRef.current?.focus()
    else complete.mutate()
  }

  const title = purpose === "reset" ? "Reset password" : "Create account"
  const submitLabel = title
  const passwordLabel = purpose === "reset" ? "New password" : "Password"
  const signIn = (
    <Link className="admin-setup-link" to="/">
      {purpose === "reset" ? "Request a new link" : "Back to sign in"}
    </Link>
  )

  return (
    <SetupPage>
      <form className="admin-auth" noValidate onSubmit={submit}>
        <h1 className="tny-wizard-title" ref={headingRef} tabIndex={-1}>
          {title}
        </h1>
        {status.isPending ? (
          <p aria-live="polite" className="admin-note">
            Checking link…
          </p>
        ) : status.isError || !status.data.valid ? (
          // The API's own words never show: a failed check is worth a retry, a dead link a new one.
          <Alert
            action={
              status.isError ? (
                <>
                  <Button
                    loading={status.isFetching}
                    onClick={() => void status.refetch()}
                    size="sm"
                    variant="secondary"
                  >
                    Try again
                  </Button>
                  {signIn}
                </>
              ) : (
                signIn
              )
            }
            tone="danger"
          >
            {status.isError
              ? "We couldn't check this link. Check your connection and try again."
              : "This link is invalid or has expired."}
          </Alert>
        ) : complete.data ? (
          <div className="admin-stack" role="status">
            <p className="admin-note">{complete.data.message}</p>
            <Link className="tny-button tny-button-primary admin-setup-action" to="/">
              Sign in
            </Link>
          </div>
        ) : (
          <>
            <Field
              error={errors.password}
              hint={
                errors.password
                  ? undefined
                  : "8 or more characters, with a number and a special character."
              }
              label={passwordLabel}
            >
              <Input
                autoComplete="new-password"
                onChange={(event) => setPassword(event.target.value)}
                ref={passwordRef}
                type="password"
                value={password}
              />
            </Field>
            <Field error={errors.confirmPassword} label="Confirm password">
              <Input
                autoComplete="new-password"
                onChange={(event) => setConfirmPassword(event.target.value)}
                ref={confirmRef}
                type="password"
                value={confirmPassword}
              />
            </Field>
            {complete.error instanceof Error ? (
              <p className="tny-error" role="alert">
                {complete.error.message}
              </p>
            ) : null}
            <Button loading={complete.isPending} type="submit">
              {submitLabel}
            </Button>
          </>
        )}
      </form>
    </SetupPage>
  )
}
